const DEFAULT_FILTERS = {
  role: "all",
  departmentId: "all",
  teamId: "all",
  localisationId: "all",
  status: "all",
};

export const filterSortAndPaginateAdminSettingsOperators = (
  operators,
  search_text,
  sortDescriptor,
  nbOperatorsPerPage,
  pageNumber,
  filters = DEFAULT_FILTERS
) => {
  if (!operators || operators.length === 0) return { operators: [], nbFiltered: 0 };

  let filtered_operators = operators.filter((operator) => !operator?.user?.is_easier_admin);

  // Applying filters
  if (search_text !== "") {
    filtered_operators = filtered_operators.filter((operator) => {
      const matches_operator_name = `${operator.first_name} ${operator.last_name}`
        ?.toLowerCase()
        ?.includes(search_text.toLowerCase());
      const matches_email = operator.email?.toLowerCase()?.includes(search_text.toLowerCase());
      const matches_team = operator?.team?.toLowerCase()?.includes(search_text.toLowerCase());
      const matches_department = operator?.department
        ?.toLowerCase()
        .includes(search_text.toLowerCase());
      const matches_localisation = operator?.localisation
        ?.toLowerCase()
        .includes(search_text.toLowerCase());
      const matches_extension_3cx = (operator?.extension_3cx || -1)
        .toString()
        .includes(search_text.toLowerCase());
      const matches_cx_one_identifier = String(operator?.cx_one_identifier || "")
        .toLowerCase()
        .includes(search_text.toLowerCase());

      return (
        matches_operator_name ||
        matches_email ||
        matches_team ||
        matches_localisation ||
        matches_department ||
        matches_extension_3cx ||
        matches_cx_one_identifier
      );
    });
  }

  const { role, departmentId, teamId, localisationId, status } = {
    ...DEFAULT_FILTERS,
    ...filters,
  };

  if (role === "admin") {
    filtered_operators = filtered_operators.filter((operator) =>
      operator?.user?.permissions?.includes("SUPER_ADMIN")
    );
  }
  if (role === "member") {
    filtered_operators = filtered_operators.filter(
      (operator) =>
        operator?.user?.permissions && !operator?.user?.permissions?.includes("SUPER_ADMIN")
    );
  }
  if (role === "no_account") {
    filtered_operators = filtered_operators.filter(
      (operator) => !operator?.noOperatorId && (operator?.user || null) === null
    );
  }

  if (departmentId !== "all") {
    filtered_operators = filtered_operators.filter(
      (operator) => operator?.department_id === departmentId
    );
  }
  if (teamId !== "all") {
    filtered_operators = filtered_operators.filter((operator) => operator?.team_id === teamId);
  }
  if (localisationId !== "all") {
    filtered_operators = filtered_operators.filter(
      (operator) => operator?.localisation_id === localisationId
    );
  }

  if (status === "active") {
    filtered_operators = filtered_operators.filter((operator) => operator?.is_active);
  }
  if (status === "inactive") {
    filtered_operators = filtered_operators.filter(
      (operator) => !operator?.noOperatorId && !operator?.is_active
    );
  }

  // Sorting
  const getSortFunction = (sortDescriptor) => {
    const { direction: sortDirection, sortBy: sortColumn } = sortDescriptor;
    const isReverted = sortDirection === "asc";

    const defaultSimpleStringSort = (a, b) => {
      const cmp = a.localeCompare(b);
      return isReverted ? -cmp : cmp;
    };

    const defaultNumberSort = (a, b) => {
      const cellValueA = a[sortColumn] || 0;
      const cellValueB = b[sortColumn] || 0;
      const cmp = cellValueB - cellValueA;

      return isReverted ? -cmp : cmp;
    };

    const sortFunctionsMapByColumnName = {
      name: (a, b) =>
        defaultSimpleStringSort(`${a.first_name} ${a.last_name}`, `${b.first_name} ${b.last_name}`),
      team: (a, b) => defaultSimpleStringSort(a.team_name || "z", b.team_name || "z"),
      department: (a, b) =>
        defaultSimpleStringSort(a.department_name || "z", b.department_name || "z"),
      localisation: (a, b) =>
        defaultSimpleStringSort(a.localisation_name || "z", b.localisation_name || "z"),
      extension_3cx: defaultNumberSort,
      cx_one_identifier: (a, b) =>
        defaultSimpleStringSort(a.cx_one_identifier || "", b.cx_one_identifier || ""),
      active: (a, b) => {
        const valueA = a?.is_active ? 1 : 0;
        const valueB = b?.is_active ? 1 : 0;

        const cmp = valueB - valueA;
        return isReverted ? -cmp : cmp;
      },
      role: (a, b) => {
        const valueA =
          a?.user === null
            ? a?.is_active
              ? -2
              : -1
            : (a?.user?.permissions || []).includes("SUPER_ADMIN")
            ? 1
            : 0;
        const valueB =
          b?.user === null
            ? b?.is_active
              ? -2
              : -1
            : (b?.user?.permissions || []).includes("SUPER_ADMIN")
            ? 1
            : 0;

        const cmp = valueB - valueA;
        return isReverted ? -cmp : cmp;
      },
    };
    return sortFunctionsMapByColumnName[sortColumn];
  };

  const compareFunction = getSortFunction(sortDescriptor);
  filtered_operators.sort(compareFunction);
  const nbFiltered = filtered_operators.length;

  // Applying pagination
  const paginationStart = pageNumber * nbOperatorsPerPage;
  const paginationEnd = (pageNumber + 1) * nbOperatorsPerPage;
  const result = filtered_operators.slice(paginationStart, paginationEnd);

  return {
    operators: result,
    nbFiltered,
  };
};
